import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface TodayPayload {
  generatedAt: string;
  clinicId: string;
  severeFindingsUnresolved: number;
  walkInsToday: number;
  followupsToday: number;
  pendingPayments: number;
  attentionInbox: number;
  followupSamples: Array<{
    id: string;
    taskType: string;
    dueAt: string;
    familyId: string;
  }>;
  pendingPaymentSamples: Array<{
    id: string;
    tier: string;
    annualPriceInr: number;
    familyId: string;
    childId: string;
  }>;
  attentionInboxSamples: Array<{
    id: string;
    body: string | null;
    intentClassification: string | null;
    sentiment: string | null;
    occurredAt: string;
  }>;
  flaggedFamilies: number;
  flaggedSamples: Array<{
    id: string;
    flagReason: string | null;
    city: string | null;
  }>;
  kpiYesterday: {
    walkInsCount: number;
    walkInSubsCount: number;
  } | null;
}

function Today() {
  const [data, setData] = useState<TodayPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<TodayPayload>("/api/today").then(setData).catch((e: Error) =>
      setErr(e.message),
    );
  }, []);

  if (err) return <div className="panel text-red-600">{err}</div>;
  if (!data) return <div className="panel text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-3">
        <Tile
          label="Severe / critical"
          value={data.severeFindingsUnresolved}
          tone={data.severeFindingsUnresolved > 0 ? "alert" : "neutral"}
        />
        <Tile
          label="Flagged families"
          value={data.flaggedFamilies}
          tone={data.flaggedFamilies > 0 ? "alert" : "neutral"}
        />
        <Tile label="Walk-ins today" value={data.walkInsToday} />
        <Tile label="Follow-ups today" value={data.followupsToday} />
        <Tile label="Pending payments" value={data.pendingPayments} />
        <Tile
          label="Attention inbox"
          value={data.attentionInbox}
          tone={data.attentionInbox > 0 ? "warn" : "neutral"}
        />
      </div>

      {data.flaggedSamples.length > 0 && (
        <div className="panel border-red-300 bg-red-50">
          <h3 className="text-sm font-medium text-red-800 mb-2">
            Flagged families needing review
          </h3>
          <ul className="text-sm space-y-1">
            {data.flaggedSamples.map((f) => (
              <li key={f.id}>
                <Link
                  to="/family/$familyId"
                  params={{ familyId: f.id }}
                  className="text-red-900 hover:underline"
                >
                  {f.city ?? f.id.slice(-8)}
                </Link>
                {f.flagReason && (
                  <span className="ml-2 text-xs text-red-700">
                    — {f.flagReason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="panel">
          <h3 className="font-medium text-sm text-slate-700 mb-2">
            Follow-ups due today
          </h3>
          {data.followupSamples.length === 0 ? (
            <div className="text-xs text-slate-400">Inbox zero.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {data.followupSamples.map((t) => (
                <li key={t.id}>
                  <Link
                    to="/family/$familyId"
                    params={{ familyId: t.familyId }}
                    className="text-slate-700 hover:underline"
                  >
                    {t.taskType}
                  </Link>{" "}
                  <span className="text-xs text-slate-400">
                    · {new Date(t.dueAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/followups" className="text-xs text-slate-500 underline">
            All follow-ups →
          </Link>
        </div>

        <div className="panel">
          <h3 className="font-medium text-sm text-slate-700 mb-2">
            Pending payment links
          </h3>
          {data.pendingPaymentSamples.length === 0 ? (
            <div className="text-xs text-slate-400">None.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {data.pendingPaymentSamples.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/family/$familyId"
                    params={{ familyId: s.familyId }}
                    className="text-slate-700 hover:underline"
                  >
                    <strong className="capitalize">{s.tier}</strong>
                  </Link>{" "}
                  <span className="text-xs text-slate-500">
                    ₹{s.annualPriceInr}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="panel">
          <h3 className="font-medium text-sm text-slate-700 mb-2">
            Attention inbox
          </h3>
          {data.attentionInboxSamples.length === 0 ? (
            <div className="text-xs text-slate-400">No flagged messages.</div>
          ) : (
            <ul className="text-sm space-y-2">
              {data.attentionInboxSamples.map((m) => (
                <li key={m.id} className="text-xs">
                  <div className="flex gap-1">
                    {m.intentClassification && (
                      <span className="rounded bg-amber-100 text-amber-800 px-1.5">
                        {m.intentClassification}
                      </span>
                    )}
                    {m.sentiment === "negative" && (
                      <span className="rounded bg-red-100 text-red-800 px-1.5">
                        negative
                      </span>
                    )}
                  </div>
                  <div className="text-slate-600 truncate">{m.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="panel text-xs text-slate-500">
        Yesterday:{" "}
        {data.kpiYesterday
          ? `${data.kpiYesterday.walkInsCount} walk-ins, ${data.kpiYesterday.walkInSubsCount} subscribed`
          : "no rollup yet (cron runs at 19:00 UTC daily)"}
        <span className="float-right">
          generated {new Date(data.generatedAt).toLocaleTimeString()} · cached 5 min
        </span>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "alert" | "warn";
}) {
  const toneClass =
    tone === "alert"
      ? "border-red-300 bg-red-50"
      : tone === "warn"
        ? "border-amber-300 bg-amber-50"
        : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${toneClass}`}>
      <div className="text-3xl font-semibold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export const Route = createFileRoute("/")({ component: Today });
